import { useState, useEffect } from "react";
import {
  Layers,
  X,
  Tag,
  Globe,
  Ship,
  Building,
  User,
  Briefcase,   
} from "lucide-react";
import { createFleet, updateFleet } from "../../../services/fleetService";
import { getAllCompanies } from "../../../services/companyService";
import { toast } from "react-hot-toast";

const AddEditFleetModal = ({ fleet, open, onClose, onSubmit }) => {
  const [form, setForm] = useState({
    name: "",
    owner: "",
    manager: "",
    region: "",
    fleetCode: "",
    company: "",
  });

  const [companies, setCompanies] = useState([]);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchCompanies = async () => {
      setIsLoadingCompanies(true);
      try {
        const response = await getAllCompanies();
        if (response.status === "success") {
          setCompanies(response.data);
        }
      } catch (error) {
        console.error("Failed to fetch companies:", error);
      } finally {
        setIsLoadingCompanies(false);
      }
    };

    if (open) {
      fetchCompanies();

      setIsLoading(true);
      if (fleet) {
        setForm({
          name: fleet.fleetName || "",
          owner: fleet.fleetOwner || "",
          manager: fleet.fleetManager || "",
          region: fleet.fleetRegion || "",
          fleetCode: fleet.fleetCode || "",
          company: fleet.company._id || "",
        });
      } else {
        setForm({
          name: "",
          owner: "",
          manager: "",
          region: "",
          fleetCode: "",
          company: "",
        });
      }
      setIsLoading(false);
    }
  }, [open, fleet]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = "Fleet name is required";
    // if (!form.owner.trim()) newErrors.owner = "Owner is required";
    // if (!form.manager.trim()) newErrors.manager = "Manager is required";
    // if (!form.region.trim()) newErrors.region = "Region is required";
    // if (!form.fleetCode.trim()) newErrors.fleetCode = "Fleet code is required";
    if (!form.company) newErrors.company = "Company is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const payload = {
        ...form,
        vessels: form.vessels,
      };

      let response;
      if (fleet && fleet.fleetId) {
        response = await updateFleet(fleet.fleetId, payload);
        toast.success("Fleet updated successfully");
      } else {
        response = await createFleet(payload);
        toast.success("Fleet created successfully");
      }

      onSubmit(response.data);
      onClose();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "Operation failed");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-in fade-in duration-200">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl flex flex-col max-h-[90vh] overflow-hidden">
        {/* FORM HEADER */}
        <div className="relative bg-white rounded-t-3xl border-b border-gray-200 px-6 pt-5 pb-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 border border-gray-300 rounded-lg flex items-center justify-center bg-gray-50">
              <Layers className="w-5 h-5 text-gray-600" />
            </div>
            <div className="flex flex-col">
              <h2 className="text-lg font-semibold text-gray-800 tracking-tight">
                {fleet ? "Edit Fleet" : "Add New Fleet"}
              </h2>
            </div>
          </div>

          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-gray-100 transition"
          >
            <X className="w-4 h-4 text-gray-600" />
          </button>
        </div>

        {/* FORM CONTENT */}
        {isLoading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className="flex-1 px-6 py-5 overflow-y-auto space-y-6 text-sm text-gray-700 bg-white">
            {/* Fleet Name */}
            <div>
              <label className="mb-1 block font-medium">Fleet Name</label>
              <div className="relative">
                <input
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Pacific Fleet"
                  className={`w-full rounded-lg border px-4 py-2.5 pr-10 bg-white placeholder:text-gray-400 focus:outline-none focus:ring-1
                                    ${
                                      errors.name
                                        ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                                        : "border-gray-300 focus:border-[#27ade3] focus:ring-[#cce5f8]"
                                    }`}
                />
                <Layers className="absolute right-3 top-3 h-4 w-4 text-gray-400" />
              </div>
              {errors.name && (
                <p className="text-red-500 text-xs mt-1">{errors.name}</p>
              )}
            </div>

            {/* Fleet Code */}
            <div>
              <label className="mb-1 block font-medium">Fleet Code</label>
              <div className="relative">
                <input
                  name="fleetCode"
                  value={form.fleetCode}
                  onChange={handleChange}
                  placeholder="FLEETA001"
                  className={`w-full rounded-lg border px-4 py-2.5 pr-10 bg-white placeholder:text-gray-400 focus:outline-none focus:ring-1
                                    ${
                                      errors.fleetCode
                                        ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                                        : "border-gray-300 focus:border-[#27ade3] focus:ring-[#cce5f8]"
                                    }`}
                />
                <Tag className="absolute right-3 top-3 h-4 w-4 text-gray-400" />
              </div>
              {errors.fleetCode && (
                <p className="text-red-500 text-xs mt-1">{errors.fleetCode}</p>
              )}
            </div>

            {/* Owner */}
            <div>
              <label className="mb-1 block font-medium">Owner</label>
              <div className="relative">
                <input
                  name="owner"
                  value={form.owner}
                  onChange={handleChange}
                  placeholder="Oceanic Corp"
                  className={`w-full rounded-lg border px-4 py-2.5 pr-10 bg-white placeholder:text-gray-400 focus:outline-none focus:ring-1
                                    ${
                                      errors.owner
                                        ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                                        : "border-gray-300 focus:border-[#27ade3] focus:ring-[#cce5f8]"
                                    }`}
                />
                <User className="absolute right-3 top-3 h-4 w-4 text-gray-400" />
              </div>
              {errors.owner && (
                <p className="text-red-500 text-xs mt-1">{errors.owner}</p>
              )}
            </div>

            {/* Manager */}
            <div>
              <label className="mb-1 block font-medium">Manager</label>
              <div className="relative">
                <input
                  name="manager"
                  value={form.manager}
                  onChange={handleChange}
                  placeholder="Marine Management Ltd"
                  className={`w-full rounded-lg border px-4 py-2.5 pr-10 bg-white placeholder:text-gray-400 focus:outline-none focus:ring-1
                                    ${
                                      errors.manager
                                        ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                                        : "border-gray-300 focus:border-[#27ade3] focus:ring-[#cce5f8]"
                                    }`}
                />
                <Briefcase className="absolute right-3 top-3 h-4 w-4 text-gray-400" />
              </div>
              {errors.manager && (
                <p className="text-red-500 text-xs mt-1">{errors.manager}</p>
              )}
            </div>

            {/* Region */}
            <div>
              <label className="mb-1 block font-medium">Region</label>
              <div className="relative">
                <input
                  name="region"
                  value={form.region}
                  onChange={handleChange}
                  placeholder="Asia Pacific"
                  className={`w-full rounded-lg border px-4 py-2.5 pr-10 bg-white placeholder:text-gray-400 focus:outline-none focus:ring-1
                                    ${
                                      errors.region
                                        ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                                        : "border-gray-300 focus:border-[#27ade3] focus:ring-[#cce5f8]"
                                    }`}
                />
                <Globe className="absolute right-3 top-3 h-4 w-4 text-gray-400" />
              </div>
              {errors.region && (
                <p className="text-red-500 text-xs mt-1">{errors.region}</p>
              )}
            </div>

            {/* Company Select */}
            <div>
              <label className="mb-1 block font-medium">Company</label>
              <div className="relative">
                <select
                  name="company"
                  value={form.company}
                  onChange={handleChange}
                  disabled={isLoadingCompanies}
                  className={`w-full rounded-lg border px-4 py-2.5 pr-10 appearance-none bg-white focus:outline-none focus:ring-1
                    ${
                      errors.company
                        ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                        : "border-gray-300 focus:border-[#27ade3] focus:ring-[#cce5f8]"
                    } ${
                    isLoadingCompanies ? "opacity-70 cursor-not-allowed" : ""
                  }`}
                >
                  <option value="">
                    {isLoadingCompanies
                      ? "Loading companies..."
                      : "Select a company"}
                  </option>
                  {companies.map((comp) => (
                    <option key={comp._id} value={comp._id}>
                      {comp.name}
                    </option>
                  ))}
                </select>
                <Building className="absolute right-3 top-3 h-4 w-4 text-gray-400 pointer-events-none" />
                {isLoadingCompanies && (
                  <div className="absolute right-10 top-3">
                    <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}
              </div>
              {errors.company && (
                <p className="text-red-500 text-xs mt-1">{errors.company}</p>
              )}
            </div>
          </div>
        )}

        {/* FORM FOOTER */}
        <div className="bg-white rounded-b-3xl border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting || isLoading}
            className={`px-5 py-2.5 text-sm rounded-lg text-white flex items-center gap-2 transition-colors font-medium 
              ${
                isSubmitting
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-[#1a325d] hover:bg-[#21426e]"
              }`}
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>{fleet ? "Updating..." : "Creating..."}</span>
              </>
            ) : (
              <span>{fleet ? "Update Fleet" : "Add Fleet"}</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddEditFleetModal;
